import { useState } from "react";
import { Link } from "react-router-dom";
import {
  aiReadinessQuestions,
  aiMaturityTiers,
  industryOptions,
  companySizeOptions,
} from "@/mocks/aiReadinessData";

interface ProfileData {
  fullName: string;
  organisation: string;
  email: string;
  telephone: string;
  industry: string;
  companySize: string;
  businessLocation: string;
  yearsInBusiness: string;
  hasWebsite: string;
  websiteUrl: string;
}

interface AssessmentAnswers {
  [key: string]: number;
}

const initialProfile: ProfileData = {
  fullName: "",
  organisation: "",
  email: "",
  telephone: "",
  industry: "",
  companySize: "",
  businessLocation: "",
  yearsInBusiness: "",
  hasWebsite: "no",
  websiteUrl: "",
};

export default function AIReadinessPage() {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState<ProfileData>(initialProfile);
  const [answers, setAnswers] = useState<AssessmentAnswers>({});
  const [activeCategory, setActiveCategory] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  

  const sendOTP = async () => {
    if (!profile.email) {
      alert("Please enter your email address");
      return;
    }
  
    try {
      setVerifying(true);
  
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/ai-readiness/send-otp/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: profile.email,
          }),
        }
      );
  
      if (response.ok) {
        setOtpSent(true);
        alert("Verification code sent to your email");
      }
    } catch (error) {
      alert("Failed to send verification code");
    } finally {
      setVerifying(false);
    }
  };


  const verifyOTP = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/ai-readiness/verify-otp/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: profile.email,
            otp,
          }),
        }
      );
  
      const data = await response.json();
  
      console.log(data);
  
      if (response.ok) {
        setEmailVerified(true);
        alert("Email verified successfully");
      } else {
        alert(data.error || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      alert("Verification failed");
    }
  };

  const totalQuestions = aiReadinessQuestions.reduce(
    (acc, cat) => acc + cat.questions.length,
    0
  );
  const answeredCount = Object.keys(answers).length;

  const handleProfileChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleAnswer = (questionId: string, score: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: score }));
  };

  const calculateScore = () => {
    const total = Object.values(answers).reduce((sum, val) => sum + val, 0);
    const percentage = Math.round((total / (totalQuestions * 4)) * 100);
    const tier =
      aiMaturityTiers.find(
        (t) => total >= t.min && total <= t.max
      ) || aiMaturityTiers[0];
    return { total, percentage, tier };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const { total, percentage, tier } = calculateScore();
    const formData = new URLSearchParams();

    Object.entries(profile).forEach(([key, value]) => {
      if (value) formData.append(key, value);
    });
    formData.append("totalScore", String(total));
    formData.append("maxScore", String(totalQuestions * 4));
    formData.append("percentage", String(percentage));
    formData.append("tier", tier.label);

    Object.entries(answers).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    try {
      await fetch(
        `${import.meta.env.VITE_API_URL}/api/ai-readiness/submit/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            profile,
            answers,
            total,
            percentage,
            tier: tier.label,
          }),
        }
      );
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  const profileComplete =
    profile.fullName &&
    profile.organisation &&
    profile.email &&
    profile.telephone &&
    profile.industry &&
    profile.companySize &&
    emailVerified;

  return (
    <div className="min-h-screen bg-white pt-20 md:pt-24 pb-16">
      {/* Hero */}
      <div className="bg-black text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-xs font-bold tracking-wider uppercase text-[#C8F135] mb-3">
            ARA
          </span>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            AI Readiness Assessment
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Discover where your business stands on the AI maturity scale. Answer
            20 questions across 7 key areas and get your personalised readiness
            report.
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 1 ? "bg-black text-[#C8F135]" : "bg-gray-200 text-gray-500"
              }`}
            >
              1
            </div>
            <div className="hidden sm:block text-sm font-medium">
              Profile
            </div>
            <div className="flex-1 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-black rounded-full transition-all"
                style={{ width: step >= 2 ? "100%" : "0%" }}
              />
            </div>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 2 ? "bg-black text-[#C8F135]" : "bg-gray-200 text-gray-500"
              }`}
            >
              2
            </div>
            <div className="hidden sm:block text-sm font-medium">
              Assessment
            </div>
            <div className="flex-1 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-black rounded-full transition-all"
                style={{ width: step >= 3 ? "100%" : "0%" }}
              />
            </div>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= 3 ? "bg-black text-[#C8F135]" : "bg-gray-200 text-gray-500"
              }`}
            >
              3
            </div>
            <div className="hidden sm:block text-sm font-medium">
              Results
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Step 1: Profile */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Your Business Profile
            </h2>
            <p className="text-gray-600 mb-6">
              Tell us about your organisation so we can tailor your AI readiness
              report.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={profile.fullName}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Organisation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="organisation"
                  value={profile.organisation}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={profile.email}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                  placeholder="john@acme.com"
                />
              </div>

              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={sendOTP}
                  disabled={verifying || emailVerified}
                  className="px-4 py-2 bg-black text-white rounded"
                >
                  {emailVerified
                    ? "Verified"
                    : otpSent
                    ? "Resend Code"
                    : "Verify Email"}
                </button>
              </div>

              {otpSent && !emailVerified && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="border px-4 py-2 rounded"
                  />

                  <button
                    type="button"
                    onClick={verifyOTP}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                  >
                    Confirm
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Telephone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={profile.telephone}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                  placeholder="+1 234 567 890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Industry / Sector <span className="text-red-500">*</span>
                </label>
                <select
                  name="industry"
                  value={profile.industry}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black bg-white"
                >
                  <option value="">Select industry</option>
                  {industryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Company Size <span className="text-red-500">*</span>
                </label>
                <select
                  name="companySize"
                  value={profile.companySize}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black bg-white"
                >
                  <option value="">Select size</option>
                  {companySizeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Business Location
                </label>
                <input
                  type="text"
                  name="businessLocation"
                  value={profile.businessLocation}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                  placeholder="City, Country"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Years in Business
                </label>
                <select
                  name="yearsInBusiness"
                  value={profile.yearsInBusiness}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black bg-white"
                >
                  <option value="">Select years</option>
                  <option value="Less than 1">Less than 1</option>
                  <option value="1-3 years">1-3 years</option>
                  <option value="3-5 years">3-5 years</option>
                  <option value="5-10 years">5-10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Do you have a website?
                </label>
                <div className="flex gap-6 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasWebsite"
                      value="yes"
                      checked={profile.hasWebsite === "yes"}
                      onChange={handleProfileChange}
                      className="w-4 h-4 accent-black"
                    />
                    <span className="text-sm text-slate-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasWebsite"
                      value="no"
                      checked={profile.hasWebsite === "no"}
                      onChange={handleProfileChange}
                      className="w-4 h-4 accent-black"
                    />
                    <span className="text-sm text-slate-700">No</span>
                  </label>
                </div>
              </div>
              {profile.hasWebsite === "yes" && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={profile.websiteUrl}
                    onChange={handleProfileChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C8F135] focus:border-black"
                    placeholder="https://www.yoursite.com"
                  />
                </div>
              )}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={() => profileComplete && setStep(2)}
                disabled={!profileComplete}
                className="px-8 py-3 rounded-full text-sm font-bold bg-black text-[#C8F135] hover:bg-[#C8F135] hover:text-black transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                Start Assessment
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Assessment */}
        {step === 2 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-slate-900">
                Assessment Questions
              </h2>
              <span className="text-sm font-medium text-gray-500">
                {answeredCount} / {totalQuestions} answered
              </span>
            </div>

            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {aiReadinessQuestions.map((cat, idx) => {
                const catAnswered = cat.questions.filter(
                  (q) => answers[q.id] !== undefined
                ).length;
                const catTotal = cat.questions.length;
                return (
                  <button
                    key={cat.category}
                    onClick={() => setActiveCategory(idx)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                      activeCategory === idx
                        ? "bg-black text-[#C8F135]"
                        : catAnswered === catTotal
                        ? "bg-[#C8F135]/20 text-black"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {cat.category}
                    <span className="ml-1 text-[10px] opacity-70">
                      ({catAnswered}/{catTotal})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Questions for active category */}
            <div className="space-y-6">
              {aiReadinessQuestions[activeCategory].questions.map((q) => (
                <div
                  key={q.id}
                  className="bg-gray-50 rounded-xl p-5 md:p-6 border border-gray-100"
                >
                  <p className="text-sm font-semibold text-slate-900 mb-4">
                    {q.text}
                  </p>
                  <div className="space-y-2">
                    {q.options.map((opt) => (
                      <label
                        key={opt.score}
                        className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-white border border-transparent hover:border-gray-200"
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={opt.score}
                          checked={answers[q.id] === opt.score}
                          onChange={() => handleAnswer(q.id, opt.score)}
                          className="w-4 h-4 accent-black cursor-pointer"
                        />
                        <span className="text-sm text-slate-700">
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => setStep(1)}
                className="text-sm font-medium text-gray-600 hover:text-slate-900 cursor-pointer"
              >
                Back to Profile
              </button>
              <div className="flex gap-3">
                {activeCategory > 0 && (
                  <button
                    onClick={() => setActiveCategory((prev) => prev - 1)}
                    className="px-5 py-2.5 rounded-full text-sm font-medium border border-gray-300 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    Previous Category
                  </button>
                )}
                {activeCategory < aiReadinessQuestions.length - 1 ? (
                  <button
                    onClick={() => setActiveCategory((prev) => prev + 1)}
                    className="px-5 py-2.5 rounded-full text-sm font-medium bg-black text-[#C8F135] hover:bg-[#C8F135] hover:text-black cursor-pointer whitespace-nowrap"
                  >
                    Next Category
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      answeredCount === totalQuestions && setStep(3)
                    }
                    disabled={answeredCount !== totalQuestions}
                    className="px-5 py-2.5 rounded-full text-sm font-medium bg-black text-[#C8F135] hover:bg-[#C8F135] hover:text-black cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    View Results
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && (
          <div>
            {!submitted ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-[#C8F135]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-bar-chart-box-line text-3xl text-black" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Your AI Readiness Report
                </h2>
                <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                  You have completed all {totalQuestions} questions. Submit your
                  assessment to generate your personalised AI maturity report.
                </p>

                <form
                  id="ara-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="max-w-xl mx-auto"
                >
                  <div className="bg-gray-50 rounded-xl p-6 md:p-8 text-left border border-gray-100 mb-6">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">
                      Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Name</span>
                        <p className="font-medium text-slate-900">
                          {profile.fullName}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Organisation</span>
                        <p className="font-medium text-slate-900">
                          {profile.organisation}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Email</span>
                        <p className="font-medium text-slate-900">
                          {profile.email}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Industry</span>
                        <p className="font-medium text-slate-900">
                          {profile.industry}
                        </p>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">
                          Questions Answered
                        </span>
                        <span className="text-sm font-bold text-slate-900">
                          {answeredCount}/{totalQuestions}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-[#C8F135] rounded-full"
                          style={{
                            width: `${(answeredCount / totalQuestions) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 rounded-full text-sm font-bold bg-black text-[#C8F135] hover:bg-[#C8F135] hover:text-black transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                  >
                    {submitting
                      ? "Generating Report..."
                      : "Generate My Report"}
                  </button>
                </form>
              </div>
            ) : (
              <div className="text-center py-8">
                {(() => {
                  const { total, percentage, tier } = calculateScore();
                  return (
                    <>
                      <div className="w-20 h-20 bg-[#C8F135]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-trophy-line text-3xl text-black" />
                      </div>
                      <h2 className="text-2xl font-bold text-slate-900 mb-1">
                        Your AI Maturity Tier
                      </h2>
                      <p className="text-gray-500 text-sm mb-8">
                        Based on {totalQuestions} questions across 7 dimensions
                      </p>

                      {/* Score card */}
                      <div className="bg-black rounded-2xl p-6 md:p-8 text-white max-w-md mx-auto mb-8">
                        <div className="text-5xl font-bold text-[#C8F135] mb-1">
                          {percentage}%
                        </div>
                        <div className="text-sm text-gray-400 mb-4">
                          {total} / {totalQuestions * 4} points
                        </div>
                        <div className="text-xl font-bold text-white mb-2">
                          {tier.label}
                        </div>
                        <p className="text-sm text-gray-400">
                          {tier.description}
                        </p>

                        {/* Progress bar */}
                        <div className="mt-6 w-full h-3 bg-gray-800 rounded-full">
                          <div
                            className="h-3 bg-[#C8F135] rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Category breakdown */}
                      <div className="max-w-2xl mx-auto text-left mb-8">
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 text-center">
                          Category Breakdown
                        </h3>
                        <div className="space-y-3">
                          {aiReadinessQuestions.map((cat) => {
                            const catScore = cat.questions.reduce(
                              (sum, q) => sum + (answers[q.id] || 0),
                              0
                            );
                            const catMax = cat.questions.length * 4;
                            const catPct = Math.round(
                              (catScore / catMax) * 100
                            );
                            return (
                              <div
                                key={cat.category}
                                className="bg-gray-50 rounded-lg p-4 border border-gray-100"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-semibold text-slate-900">
                                    {cat.category}
                                  </span>
                                  <span className="text-sm font-bold text-slate-900">
                                    {catScore}/{catMax}
                                  </span>
                                </div>
                                <div className="w-full h-2 bg-gray-200 rounded-full">
                                  <div
                                    className="h-2 bg-black rounded-full"
                                    style={{ width: `${catPct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link
                          to="/contact"
                          className="px-6 py-3 rounded-full text-sm font-bold bg-black text-[#C8F135] hover:bg-[#C8F135] hover:text-black transition-all cursor-pointer whitespace-nowrap text-center"
                        >
                          Book a Consultation
                        </Link>
                        <button
                          onClick={() => {
                            setStep(1);
                            setAnswers({});
                            setSubmitted(false);
                            setProfile(initialProfile);
                            setActiveCategory(0);
                          }}
                          className="px-6 py-3 rounded-full text-sm font-bold border border-gray-300 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                        >
                          Retake Assessment
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}